import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { getIn } from 'formik'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import Checkbox from '../../../../components/Form/Checkbox'

import useStyles from './styles'

const CheckboxGroup = ({
  values,
  setFieldValue,
  itemPath = '',
  title,
  options,
  showSelectAllButton = true,
  disabled
}) => {
  const classes = useStyles()
  const { t } = useTranslation()

  const [areAllSelected, setAreAllSelected] = useState(false)

  const group = getIn(values, itemPath)

  const availableOptions = useMemo(() => options.filter(({ disabled }) => !disabled), [options])

  const selectAllHandler = useCallback(() => {
    const valuesToSet = {}

    availableOptions.forEach(({ name }) => {
      valuesToSet[name] = !areAllSelected
    })

    setFieldValue(itemPath, {
      ...values[itemPath],
      ...valuesToSet
    })
  }, [setFieldValue, itemPath, values, areAllSelected, availableOptions])

  const onCheckHandler = useCallback(
    (name, isChecked) => {
      setFieldValue(itemPath, {
        ...values[itemPath],
        [name]: !isChecked
      })
    },
    [itemPath, values, setFieldValue]
  )

  useEffect(() => {
    let hasNotSelected = false

    availableOptions.forEach(({ name }) => {
      if (!values[itemPath][name]) {
        hasNotSelected = true
      }
    })

    if (hasNotSelected) {
      setAreAllSelected(false)
    } else {
      setAreAllSelected(true)
    }
  }, [values, options, itemPath, availableOptions])

  return (
    <div className={classes.wrapper}>
      <div className={classes.header}>
        {title && <h3 className={classes.title}>{title}</h3>}
        {showSelectAllButton && (
          <div className={classes.selectController} onClick={selectAllHandler}>
            {t(areAllSelected ? 'Deselect all' : 'Select all')}
          </div>
        )}
      </div>
      {options.map(({ name, title, titleColor, disabled: disabledOption }) => {
        const isChecked = group[name]

        return (
          <Checkbox
            disabled={disabled || disabledOption}
            key={name}
            id={`checkbox_${name}`}
            title={t(title)}
            isLarge
            className={classes.checkbox}
            titleColor={titleColor}
            checked={isChecked}
            onCheck={() => onCheckHandler(name, isChecked)}
          />
        )
      })}
    </div>
  )
}

CheckboxGroup.propTypes = {
  values: PropTypes.object.isRequired,
  setValues: PropTypes.func.isRequired,
  itemPath: PropTypes.string,
  setFieldValue: PropTypes.func.isRequired,
  options: PropTypes.array.isRequired,
  title: PropTypes.string
}

export default CheckboxGroup
